/**
 * LE CHIUSURE AZIENDALI, senza il disegno: i controlli prima di salvare e il resoconto dopo.
 *
 * Gli stessi di Lares (`utility/leave/utils-company-closures.js`), che a loro volta ripetono
 * `CompanyClosureResourceDefinition` del server: nome, date in ordine, al massimo un anno (366
 * giorni fra la prima e l'ultima), le mezze giornate ammesse, e su un giorno solo non «dal
 * pomeriggio» e «solo la mattina» insieme. Detti prima, perché il 422 del server arriva dopo che si
 * è già premuto «Crea», e dice il campo in inglese.
 *
 * Il resoconto è il `meta` **della risorsa** che il server mette nella risposta di ogni scrittura
 * (`GetMeta`, BE 08-10-2026 §8.3): quante persone sono coinvolte (`affectedEmployees`) e chi è
 * andato sotto zero (`negativeBalances`). L'adapter lo conserva sotto `RESOURCE_META`.
 */
import { toDateOnly } from '@/shared/lib/format';
import { RESOURCE_META, type Denormalized } from '@/shared/api/jsonapi/parser';

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` → `DD/MM/YYYY`; vuoto se non è una data. */
function dayLabel(iso: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
}

/**
 * Cosa impedisce di salvare la chiusura, una frase per problema; vuoto se si può salvare.
 * Accetta i valori del form: le date possono essere `Date` (riga aperta) o stringhe (picker).
 */
export function closureIssues(v: Record<string, unknown>): string[] {
  const out: string[] = [];
  if (!String(v.name ?? '').trim()) out.push('Scrivi il nome della chiusura.');
  const from = toDateOnly(v.fromDate as string | Date | null);
  const to = toDateOnly(v.toDate as string | Date | null);
  const fromPart = v.fromPartOfDay || 'full';
  const toPart = v.toPartOfDay || 'full';
  if (!from || !to) {
    out.push('Scegli il primo e l’ultimo giorno.');
  } else if (to < from) {
    out.push('L’ultimo giorno viene prima del primo.');
  } else {
    const days = (Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / DAY_MS;
    if (days > 366) out.push('Una chiusura dura al massimo un anno: accorcia il periodo o dividilo in due.');
    if (from === to && fromPart === 'afternoon' && toPart === 'morning') {
      out.push('Su un giorno solo si chiude la mattina o il pomeriggio, non tutti e due: per il giorno intero lascia «Tutto il giorno».');
    }
  }
  if (fromPart !== 'full' && fromPart !== 'afternoon') out.push('Il primo giorno si chiude tutto o dal pomeriggio.');
  if (toPart !== 'full' && toPart !== 'morning') out.push('L’ultimo giorno si chiude tutto o solo la mattina.');
  return out;
}

/**
 * La colonna «Dove» di una chiusura: le sedi se ci sono (allora non scende), altrimenti l'azienda e
 * fin dove scende — tutto il gruppo, o quante aziende (task 3579). I nomi delle aziende non stanno
 * sulla riga, quindi qui si contano.
 */
export function closureWhereText(row: unknown): string {
  const r = (row ?? {}) as { sites?: { name?: string }[]; scope?: unknown; scopeTenantIds?: unknown };
  const sites = r.sites ?? [];
  if (sites.length) return sites.map((x) => x.name).filter(Boolean).join(', ');
  if (r.scope !== 'group') return 'Tutta l’azienda';
  const n = Array.isArray(r.scopeTenantIds) ? new Set(r.scopeTenantIds.map((x) => String(x).toLowerCase())).size : 0;
  if (!n) return 'Tutta l’azienda, e le aziende del gruppo';
  return `Tutta l’azienda, e ${n === 1 ? 'un’azienda' : `${n} aziende`} del gruppo`;
}

/**
 * Chi lavora lo stesso, in una frase: persone e gruppi si sommano (task 3578). Vuota se nessuno.
 */
export function closureExclusionText(row: unknown): string {
  const r = (row ?? {}) as { excludedEmployees?: unknown[]; excludedBusinessGroups?: unknown[] };
  const people = Array.isArray(r.excludedEmployees) ? r.excludedEmployees.length : 0;
  const groups = Array.isArray(r.excludedBusinessGroups) ? r.excludedBusinessGroups.length : 0;
  const parts = [
    people ? (people === 1 ? 'una persona' : `${people} persone`) : '',
    groups ? (groups === 1 ? 'un gruppo' : `${groups} gruppi`) : '',
  ].filter(Boolean);
  return parts.join(' e ');
}

/** «dal 10/08/2026 al 21/08/2026», «il 24/12/2026 dal pomeriggio». */
export function closurePeriodLabel(c: { fromDate?: unknown; toDate?: unknown; fromPartOfDay?: unknown; toPartOfDay?: unknown }): string {
  const from = toDateOnly(c.fromDate as string | null);
  const to = toDateOnly(c.toDate as string | null);
  if (!from) return '';
  const afternoon = c.fromPartOfDay === 'afternoon' ? ' dal pomeriggio' : '';
  const morning = c.toPartOfDay === 'morning' ? ' (solo la mattina)' : '';
  if (!to || to === from) return `il ${dayLabel(from)}${afternoon}${morning}`;
  return `dal ${dayLabel(from)}${afternoon} al ${dayLabel(to)}${morning}`;
}

export interface NegativeBalance {
  employeeId?: string;
  employeeName?: string;
  leaveTypeId?: string;
  year?: number;
  availableDays?: number;
}

export interface ClosureSaveReport {
  name: string;
  affectedEmployees: number;
  negativeBalances: NegativeBalance[];
}

/**
 * Il resoconto di una scrittura, dalla riga che `createOne`/`updateOne` restituisce; `null` se il
 * server non l'ha mandato (una risposta senza corpo, o un server di prima del 08-10-2026): in quel
 * caso non si dice niente, invece di dire «zero persone».
 */
export function closureSaveReport(saved: Denormalized | null | undefined): ClosureSaveReport | null {
  if (!saved) return null;
  const meta = (saved as Record<string, unknown>)[RESOURCE_META] as Record<string, unknown> | undefined;
  if (!meta || !('affectedEmployees' in meta)) return null;
  return {
    name: String((saved as { name?: unknown }).name ?? ''),
    affectedEmployees: Number(meta.affectedEmployees) || 0,
    negativeBalances: Array.isArray(meta.negativeBalances) ? (meta.negativeBalances as NegativeBalance[]) : [],
  };
}

/** «Ada Rossi (2026): -1,5 giorni», una riga per saldo sotto zero. */
export function negativeBalanceLines(list: readonly NegativeBalance[]): string[] {
  return list.map((n) => {
    const days = Number(n?.availableDays);
    const amount = Number.isFinite(days) ? days.toLocaleString('it-IT', { maximumFractionDigits: 1 }) : '—';
    return `${n?.employeeName || 'Una persona'} (${n?.year ?? '—'}): ${amount} giorni`;
  });
}
